import { computed, reactive, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { apiFetchUrl, localeMapping } from '@shared/http/client';
import { i18n } from '@shared/i18n';
import { useUserStore } from '@shared/stores/user';

export interface TranslationOptions {
    formality?: string
    context?: string
    preserve_formatting?: boolean
}

export type TranslatableOrString = string | { original: string, translation?: string | null, options?: TranslationOptions };

interface PendingRequest {
    text: string
    options: TranslationOptions | undefined
    resolve: (translated: string) => void
}

// Module-level reactive cache — shared across all component instances
const translationCache = reactive(new Map<string, string>());
// Keys whose request has SETTLED, successfully or not. Angular keeps this signal separate from the
// cache: the batch subject emits on the error path too (`subject.next(original)`), and the `tap` in
// I18nService.getTranslatedText flips `translationExists` on that emission — while the cache stays
// untouched so a later attempt can still succeed. Collapsing both into the cache left every failed
// message under its loading overlay forever.
const settledKeys = reactive(new Set<string>());
let pendingRequests: PendingRequest[] = [];
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const BATCH_DELAY_MS = 300;

export function getCurrentLocale(): string {
    // Single source of truth = displayed i18n locale (set via loadLocale in the
    // router). The old pathname regex only matched the web build's /<lang>/ path
    // and silently returned 'de' in native (hash routing), hiding the translate
    // toggle and skipping the initial translation for non-German locales.
    return i18n.global.locale.value;
}

function getCacheKey(text: string, locale: string, options?: TranslationOptions): string {
    return JSON.stringify({ text, locale, options });
}

function settleWithOriginal(requests: PendingRequest[], locale: string): void {
    requests.forEach((req) => {
        settledKeys.add(getCacheKey(req.text, locale, req.options));
        req.resolve(req.text);
    });
}

async function flushBatch(requests: PendingRequest[], locale: string): Promise<void> {
    // Without a token the translation firewall answers 401 with `WWW-Authenticate: Basic`, which
    // makes the browser show its native login dialog. Angular sends `Bearer null` there, gets a
    // Bearer 401 and falls back to the original text — same result, without the request.
    if (!useUserStore().token) {
        settleWithOriginal(requests, locale);
        return;
    }

    // Group requests by options so we send one API call per options-combination
    const groups = new Map<string, PendingRequest[]>();
    for (const req of requests) {
        const key = JSON.stringify(req.options);
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key)!.push(req);
    }

    for (const [, group] of groups) {
        const texts = group.map((r) => r.text);
        try {
            const { apiHost } = window.kgrDynamicConfig;
            const response = await apiFetchUrl(`${apiHost}/apis/translation/translations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    texts,
                    targetLanguage: localeMapping[locale] ?? `${locale}_${locale.toUpperCase()}`,
                    sourceLanguage: null,
                    options: group[0].options ?? { formality: 'prefer_more' },
                    context: group[0].options?.context ?? null,
                }),
            });
            const result = (await response.json()) as { data: Array<{ translatedText: string }> };
            group.forEach((req, i) => {
                const translated = result.data[i]?.translatedText ?? req.text;
                const key = getCacheKey(req.text, locale, req.options);
                translationCache.set(key, translated);
                settledKeys.add(key);
                req.resolve(translated);
            });
        } catch {
            // Fallback to original text on error — do not cache errors (Angular does the same, so a
            // later attempt is still free to succeed). Only record that this request is done, which
            // is what releases the loading overlay. A re-render therefore still re-queues the text;
            // that retry is deliberate and is what makes a transient failure self-healing.
            settleWithOriginal(group, locale);
        }
    }
}

function queueTranslation(text: string, options: TranslationOptions | undefined): void {
    const locale = getCurrentLocale();

    const key = getCacheKey(text, locale, options);
    if (translationCache.has(key)) {
        return;
    }

    const alreadyPending = pendingRequests.some((r) => r.text === text && JSON.stringify(r.options) === JSON.stringify(options));
    if (alreadyPending) {
        return;
    }

    pendingRequests.push({
        text,
        options,
        resolve: () => {},
    });

    if (!batchTimer) {
        batchTimer = setTimeout(() => {
            const toProcess = [...pendingRequests];
            pendingRequests = [];
            batchTimer = null;
            // Übersetzung ist best-effort: Schlägt der Call fehl (z. B. in der
            // Dev-Umgebung ohne Translation-Backend), wird bereits pro Gruppe auf
            // den Originaltext zurückgefallen. Dieser .catch() ist nur ein
            // Sicherheitsnetz gegen unhandled rejections — nie soll ein
            // fehlgeschlagener Übersetzungs-Call die App zum Absturz bringen.
            void flushBatch(toProcess, getCurrentLocale()).catch(() => {});
        }, BATCH_DELAY_MS);
    }
}

/**
 * Fetches the translation of `val` right away and resolves once it is cached (or has failed). For
 * callers that have to translate while they still hold a token, e.g. right before a logout.
 */
export async function prefetchTranslation(val: TranslatableOrString | null | undefined): Promise<void> {
    if (!val || typeof val === 'string' || val.translation) {
        return;
    }
    const locale = getCurrentLocale();
    if (translationCache.has(getCacheKey(val.original, locale, val.options))) {
        return;
    }
    await flushBatch([{ text: val.original, options: val.options, resolve: () => {} }], locale);
}

/**
 * Synchronous helper for use in v-for loops / template expressions.
 * Reads from the reactive translationCache (triggers re-render when updated)
 * and queues a translation fetch if needed.
 * showOriginal defaults to false (show translation when available).
 */
export function resolveTranslatable(val: TranslatableOrString | null | undefined, showOriginal = false): string {
    if (!val) {
        return '';
    }
    if (typeof val === 'string') {
        return val;
    }
    if (showOriginal) {
        return val.original;
    }
    if (val.translation) {
        return val.translation;
    }
    const locale = getCurrentLocale();
    const key = getCacheKey(val.original, locale, val.options);
    const cached = translationCache.get(key);
    if (cached === undefined) {
        queueTranslation(val.original, val.options);
    }
    return cached ?? val.original;
}

/**
 * Angular's `translationExists` signal (chat thread entry, news detail): false while a translation
 * is still being fetched. The thread entry lays a loading overlay over the message until it flips.
 * Reads the reactive cache, so callers re-render when the batch resolves.
 */
export function hasTranslation(val: TranslatableOrString | null | undefined): boolean {
    if (!val || typeof val === 'string') {
        return true;
    }
    if (val.translation) {
        return true;
    }
    const key = getCacheKey(val.original, getCurrentLocale(), val.options);
    return translationCache.get(key) !== undefined || settledKeys.has(key);
}

/**
 * Returns a computed string for a TranslatableOrString value.
 * When no pre-existing translation is present, triggers
 * POST /apis/translation/translations (batched, cached) — including for the
 * German locale, mirroring Angular's I18nService.getTranslatedText (e.g. a
 * foreign-language chat message is translated into German for a German user).
 * The returned computed ref updates reactively when the translation arrives.
 */
export function useTranslatableText(
    getter: () => TranslatableOrString | null | undefined,
    showOriginal: Ref<boolean>,
): ComputedRef<string> {
    const locale = getCurrentLocale();

    watch(
        () => [getter(), showOriginal.value] as const,
        ([val, isShowOriginal]) => {
            if (isShowOriginal) {
                return;
            }
            if (!val || typeof val === 'string') {
                return;
            }
            if (val.translation) {
                return;
            }
            queueTranslation(val.original, val.options);
        },
        { immediate: true },
    );

    return computed(() => {
        const val = getter();
        if (!val) {
            return '';
        }
        if (typeof val === 'string') {
            return val;
        }
        if (showOriginal.value) {
            return val.original;
        }
        if (val.translation) {
            return val.translation;
        }
        return translationCache.get(getCacheKey(val.original, locale, val.options)) ?? val.original;
    });
}
